import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  useTSQBOMRev,
  useTSMCreateBOMRev,
  useTSMUpdateBOMRev,
} from "../../hooks/useTSBOMs";
import { useTSQItemList } from "../../hooks/useTSItems";
import { useTSQUOMUnitList } from "../../hooks/useTSUnits";
import { ListRequestFilter } from "../../types/common/commonTypes";
import { ItemFull } from "../../types/itemTypes";
import { UOMUnitFull } from "../../types/uomTypes";
import {
  BOMRevCreate,
  BOMRevUpdate,
  BOMCompCreate,
  BOMCompUpdate,
} from "../../types/bomTypes";

interface BOMFormData {
  name: string;
  item_id: string;
  revisions: BOMRevData[];
}

interface BOMRevData {
  id?: string;
  version_name: string;
  version_number: number;
  effective_date: string;
  expiration_date?: string;
  notes?: string;
  is_new: boolean;
  is_edit: boolean;
  is_delete: boolean;
  components: BOMCompData[];
}

interface BOMCompData {
  id?: string;
  quantity: number;
  offset: number;
  line: number;
  build_as_needed: boolean;
  notes?: string;
  component_item_id: string;
  uom_unit_id: string;
  is_new: boolean;
  is_edit: boolean;
  is_delete: boolean;
}

const bomSchema = yup.object({
  name: yup.string().required("BOM name is Required"),
  item_id: yup.string().required("Item ID is Required"),
  revisions: yup.array().of(
    yup.object({
      version_name: yup.string().required(),
      version_number: yup.number().nullable(),
      effective_date: yup.string().required(),
      expiration_date: yup.string().nullable(),
      notes: yup.string().nullable(),
      is_new: yup.boolean().required(),
      is_edit: yup.boolean().required(),
      is_delete: yup.boolean().required(),
      components: yup.array().of(
        yup.object({
          quantity: yup.number().required(),
          offset: yup.number().required(),
          line: yup.number().required(),
          build_as_needed: yup.boolean().required(),
          notes: yup.string().nullable(),
          component_item_id: yup.string().required(),
          uom_unit_id: yup.string().required(),
          is_new: yup.boolean().required(),
          is_edit: yup.boolean().required(),
          is_delete: yup.boolean().required(),
        }),
      ),
    }),
  ),
});

const CompLine: React.FC<{
  id?: string;
  index: number;
  parentIndex: number;
  control: any;
  setValue: any;
  availItems: ItemFull[];
  allUoms: UOMUnitFull[];
  parentItemId: string;
  isLoading: boolean;
  errors: any;
  isEditMode: boolean;
  isViewMode: boolean;
  isCreateMode: boolean;
  remove: (index: number) => void;
}> = ({
  id = undefined,
  index,
  parentIndex,
  control,
  setValue,
  availItems = [],
  allUoms = [],
  parentItemId,
  isLoading,
  isEditMode,
  isViewMode,
  isCreateMode,
  errors,
  remove,
}) => {
  const [
    isNewLine,
    isEditLine,
    isDeleteLine,
    watchedLineNumber,
    watchedItemId,
    watchedUOMId,
    watchedQuant,
    watchedNotes,
    watchedBuildAsNeeded,
  ] = useWatch({
    control,
    name: [
      `revisions.${parentIndex}.components.${index}.is_new`,
      `revisions.${parentIndex}.components.${index}.is_edit`,
      `revisions.${parentIndex}.components.${index}.is_delete`,
      `revisions.${parentIndex}.components.${index}.line`,
      `revisions.${parentIndex}.components.${index}.component_item_id`,
      `revisions.${parentIndex}.components.${index}.uom_unit_id`,
      `revisions.${parentIndex}.components.${index}.quantity`,
      `revisions.${parentIndex}.components.${index}.notes`,
      `revisions.${parentIndex}.components.${index}.build_as_needed`,
    ],
  });

  const [selectedItem, setSelectedItem] = useState<ItemFull | null>(null);
  const [availUOMs, setAvailUOMs] = useState<UOMUnitFull[]>([]);
  const [selectedUOM, setSelectedUOM] = useState<UOMUnitFull | null>(null);

  const [itemResolved, setItemResolved] = useState<boolean>(false);
  const [initItem, setInitItem] = useState<boolean>(false);
  const [origItem, setOrigItem] = useState<string | null>(null);
  const [initUnit, setInitUnit] = useState<boolean>(false);
  const [origUnit, setOrigUnit] = useState<string | null>(null);
  const [initQuant, setInitQuant] = useState<boolean>(false);
  const [origQuant, setOrigQuant] = useState<number | null>(null);
  const [initNotes, setInitNotes] = useState<boolean>(false);
  const [origNotes, setOrigNotes] = useState<string | null>(null);
  const [initBuild, setInitBuild] = useState<boolean>(false);
  const [origBuild, setOrigBuild] = useState<boolean | null>(null);

  useEffect(() => {
    if (!initItem) {
      setOrigItem(watchedItemId);
      setInitItem(true);
    }
    if (watchedItemId && watchedItemId !== "") {
      const item = availItems.find((i) => i.id === watchedItemId)!;
      setSelectedItem(item);
      setAvailUOMs(allUoms.filter((u) => u.uom_type_id === item.uom_type.id));
      if (isNewLine || isDeleteLine) {
        setItemResolved(true);
        return;
      }
      if (watchedItemId !== origItem) {
        setValue(`revisions.${parentIndex}.components.${index}.is_edit`, true);
      }
    }
    setItemResolved(true);
  }, [
    watchedItemId,
    availItems,
    allUoms,
    index,
    initItem,
    origItem,
    setValue,
    parentIndex,
    isNewLine,
    isDeleteLine,
  ]);

  useEffect(() => {
    if (!initUnit) {
      setOrigUnit(watchedUOMId);
      setInitUnit(true);
    }
    if (!itemResolved) {
      return;
    }
    if (watchedUOMId && watchedUOMId !== "") {
      const uom = allUoms.find((u) => u.id === watchedUOMId)!;
      setSelectedUOM(uom);
      if (isNewLine || isDeleteLine) {
        return;
      }
      if (watchedUOMId !== origUnit) {
        setValue(`revisions.${parentIndex}.components.${index}.is_edit`, true);
      }
    }
  }, [
    itemResolved,
    watchedUOMId,
    allUoms,
    index,
    initUnit,
    origUnit,
    setValue,
    parentIndex,
    isNewLine,
    isDeleteLine,
  ]);

  useEffect(() => {
    if (isNewLine || isDeleteLine) {
      return;
    }
    if (!initQuant) {
      setOrigQuant(watchedQuant);
      setInitQuant(true);
    } else if (watchedQuant !== origQuant) {
      setValue(`revisions.${parentIndex}.components.${index}.is_edit`, true);
    }
  }, [
    watchedQuant,
    index,
    initQuant,
    origQuant,
    setValue,
    parentIndex,
    isNewLine,
    isDeleteLine,
  ]);

  useEffect(() => {
    if (isNewLine || isDeleteLine) {
      return;
    }
    if (!initNotes) {
      setOrigNotes(watchedNotes);
      setInitNotes(true);
    } else if (watchedNotes !== origNotes) {
      setValue(`revisions.${parentIndex}.components.${index}.is_edit`, true);
    }
  }, [
    watchedNotes,
    index,
    initNotes,
    origNotes,
    setValue,
    parentIndex,
    isNewLine,
    isDeleteLine,
  ]);

  useEffect(() => {
    if (isNewLine || isDeleteLine) {
      return;
    }
    if (!initBuild) {
      setOrigBuild(watchedBuildAsNeeded);
      setInitBuild(true);
    } else if (watchedBuildAsNeeded !== origBuild) {
      setValue(`revisions.${parentIndex}.components.${index}.is_edit`, true);
    }
  }, [
    watchedBuildAsNeeded,
    index,
    initBuild,
    origBuild,
    setValue,
    parentIndex,
    isNewLine,
    isDeleteLine,
  ]);

  const handleCompRemove = () => {
    if (isNewLine) {
      remove(index);
    } else {
      setValue(
        `revisions.${parentIndex}.components.${index}.is_delete`,
        !isDeleteLine,
      );
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  } else if (isViewMode) {
    return (
      <>
        <ListItem>
          <ListItemText>{watchedLineNumber}</ListItemText>
          <ListItemText>{selectedItem?.number || "Unkown Item"}</ListItemText>
          <ListItemText>{`${watchedQuant} (${selectedUOM?.unit_symbol})`}</ListItemText>
          <ListItemText>{watchedNotes || "-"}</ListItemText>
          <ListItemText>
            {watchedBuildAsNeeded ? "Yes" : "From Stock"}
          </ListItemText>
        </ListItem>
      </>
    );
  } else {
    return (
      <>
        <TableRow>
          <TableCell>
            <Typography variant="body2" sx={{ textWrap: "nowrap" }}>
              {`#${index + 1}`}
            </Typography>
          </TableCell>
          <TableCell>
            <Stack direction="column" gap={2}>
              <Controller
                name={`revisions.${parentIndex}.components.${index}.component_item_id`}
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth disabled={isViewMode}>
                    <InputLabel>Item</InputLabel>
                    <Select {...field} label="Item">
                      {availItems
                        .filter((item) => item.id !== parentItemId)
                        .map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.number}
                          </MenuItem>
                        ))}
                    </Select>
                    {errors.item_id && (
                      <FormHelperText>{errors.item_id.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
              <Controller
                name={`revisions.${parentIndex}.components.${index}.uom_unit_id`}
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth disabled={isViewMode}>
                    <InputLabel>UOM</InputLabel>
                    <Select {...field} label="UOM">
                      {availUOMs.map((uom) => (
                        <MenuItem key={uom.id} value={uom.id}>
                          {uom.unit_symbol}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Stack>
          </TableCell>
          <TableCell>
            <Stack direction="column" gap={2}>
              <Controller
                name={`revisions.${parentIndex}.components.${index}.quantity`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Component Quantity"
                    type="number"
                  />
                )}
              />
              <Controller
                name={`revisions.${parentIndex}.components.${index}.offset`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Per Built Quantity"
                    type="number"
                  />
                )}
              />
            </Stack>
          </TableCell>
          <TableCell>
            <Controller
              name={`revisions.${parentIndex}.components.${index}.notes`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type="text"
                  label="Revision Notes"
                  size="medium"
                  error={!!error}
                  disabled={isViewMode}
                />
              )}
            />
          </TableCell>
          <TableCell>
            <Controller
              name={`revisions.${parentIndex}.components.${index}.build_as_needed`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={field.onChange}
                      size="small"
                    />
                  }
                  label="Build As Needed?"
                />
              )}
            />
          </TableCell>
          <TableCell>
            <Stack direction="column">
              {(isCreateMode || isEditMode) && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleCompRemove}
                  size="small"
                  startIcon={<DeleteIcon />}
                >
                  Delete
                </Button>
              )}
            </Stack>
          </TableCell>
        </TableRow>
      </>
    );
  }
};

const RevLine: React.FC<{
  id?: string;
  index: number;
  control: any;
  setValue: any;
  availItems: ItemFull[];
  allUoms: UOMUnitFull[];
  parentItemId: string;
  isLoading: boolean;
  errors: any;
  isEditMode: boolean;
  isViewMode: boolean;
  isCreateMode: boolean;
  remove: (index: number) => void;
}> = ({
  id = undefined,
  index,
  control,
  setValue,
  availItems = [],
  allUoms = [],
  parentItemId,
  isLoading,
  isEditMode,
  isViewMode,
  isCreateMode,
  errors,
  remove,
}) => {
  const [compsOpen, setCompsOpen] = useState<boolean>(false);

  const [
    isNewLine,
    isEditLine,
    isDeleteLine,
    watchedVersionNum,
    watchedVersionName,
    watchedEffDate,
    watchedExpDate,
    watchedNotes,
  ] = useWatch({
    control,
    name: [
      `revisions.${index}.is_new`,
      `revisions.${index}.is_edit`,
      `revisions.${index}.is_delete`,
      `revisions.${index}.version_number`,
      `revisions.${index}.version_name`,
      `revisions.${index}.effective_date`,
      `revisions.${index}.expiration_date`,
      `revisions.${index}.notes`,
    ],
  });

  const {
    fields: components,
    append: compAppend,
    remove: compRemove,
  } = useFieldArray({
    control,
    name: `revisions.${index}.components`,
  });

  const handleRevRemove = () => {
    if (isNewLine) {
      remove(index);
    } else {
      setValue(`revisions.${index}.is_delete`, !isDeleteLine);
    }
  };

  const [initName, setInitName] = useState<boolean>(false);
  const [origName, setOrigName] = useState<string | null>(null);
  const [initEff, setInitEff] = useState<boolean>(false);
  const [origEff, setOrigEff] = useState<string | null>(null);
  const [initExp, setInitExp] = useState<boolean>(false);
  const [origExp, setOrigExp] = useState<string | null>(null);
  const [initNotes, setInitNotes] = useState<boolean>(false);
  const [origNotes, setOrigNotes] = useState<string | null>(null);

  useEffect(() => {
    if (isNewLine || isDeleteLine) {
      return;
    }
    if (!initName) {
      setOrigName(watchedVersionName);
      setInitName(true);
    } else if (watchedVersionName !== origName) {
      setValue(`revisions.${index}.is_edit`, true);
    }
  }, [
    isNewLine,
    isDeleteLine,
    watchedVersionName,
    initName,
    origName,
    setValue,
    index,
  ]);

  useEffect(() => {
    if (isNewLine || isDeleteLine) {
      return;
    }
    if (!initEff) {
      setOrigEff(watchedEffDate);
      setInitEff(true);
    } else if (watchedEffDate !== origEff) {
      setValue(`revisions.${index}.is_edit`, true);
    }
  }, [
    isNewLine,
    isDeleteLine,
    watchedEffDate,
    initEff,
    origEff,
    setValue,
    index,
  ]);

  useEffect(() => {
    if (isNewLine || isDeleteLine) {
      return;
    }
    if (!initExp) {
      setOrigExp(watchedExpDate);
      setInitExp(true);
    } else if (watchedExpDate !== origExp) {
      setValue(`revisions.${index}.is_edit`, true);
    }
  }, [
    isNewLine,
    isDeleteLine,
    watchedExpDate,
    initExp,
    origExp,
    setValue,
    index,
  ]);

  useEffect(() => {
    if (isNewLine || isDeleteLine) {
      return;
    }
    if (!initNotes) {
      setOrigNotes(watchedNotes);
      setInitNotes(true);
    } else if (watchedNotes !== origNotes) {
      setValue(`revisions.${index}.is_edit`, true);
    }
  }, [
    isNewLine,
    isDeleteLine,
    watchedNotes,
    initNotes,
    origNotes,
    setValue,
    index,
  ]);

  const addCompLine = () => {
    compAppend({
      quantity: 0,
      offset: 1,
      line: 999,
      build_as_needed: false,
      notes: undefined,
      component_item_id: "",
      uom_unit_id: "",
      is_new: true,
      is_edit: true,
      is_delete: false,
    });
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <TableRow>
        <TableCell>
          <Typography variant="body2" sx={{ textWrap: "nowrap" }}>
            {`#${index + 1}${isDeleteLine ? " (DELETED)" : ""}`}
          </Typography>
        </TableCell>
        <TableCell>
          {isViewMode ? (
            <Typography variant="body2">{watchedVersionName}</Typography>
          ) : (
            <Controller
              name={`revisions.${index}.version_name`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Version Name"
                  disabled={isViewMode}
                  size="small"
                />
              )}
            />
          )}
        </TableCell>
        <TableCell>
          {isViewMode ? (
            <Stack direction="column" gap={2}>
              <Typography variant="body2">
                {new Date(watchedEffDate).toLocaleDateString()}
              </Typography>
              <Typography variant="body2">
                {watchedExpDate
                  ? new Date(watchedExpDate).toLocaleDateString()
                  : "-"}
              </Typography>
            </Stack>
          ) : (
            <Stack direction="column" gap={2}>
              <Controller
                name={`revisions.${index}.effective_date`}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    label="Effective Date"
                    value={field.value ? new Date(field.value) : null}
                    onChange={(date) => field.onChange(date?.toISOString())}
                    disabled={isViewMode}
                    slotProps={{
                      textField: {
                        error: !!errors.effective_date,
                        helperText: errors.effective_date?.message,
                      },
                    }}
                  />
                )}
              />
              <Controller
                name={`revisions.${index}.expiration_date`}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    label="Expiration Date"
                    value={field.value ? new Date(field.value) : null}
                    onChange={(date) => field.onChange(date?.toISOString())}
                    disabled={isViewMode}
                    slotProps={{
                      textField: {
                        error: !!errors.expiration_date,
                        helperText: errors.expiration_date?.message,
                      },
                    }}
                  />
                )}
              />
            </Stack>
          )}
        </TableCell>
        <TableCell>
          {isViewMode ? (
            <Typography variant="body2">{watchedNotes}</Typography>
          ) : (
            <Controller
              name={`revisions.${index}.notes`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type="text"
                  label="Revision Notes"
                  size="medium"
                  error={!!error}
                  disabled={isViewMode}
                />
              )}
            />
          )}
        </TableCell>
        <TableCell>
          <Stack direction="column">
            {(isCreateMode || isEditMode) && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleRevRemove}
                size="small"
                startIcon={<DeleteIcon />}
              >
                {isNewLine ? "Delete" : "Deactivate"}
              </Button>
            )}
            {compsOpen && (isCreateMode || isEditMode) && (
              <Button
                variant="outlined"
                color="error"
                onClick={addCompLine}
                size="small"
                startIcon={<AddIcon />}
              >
                Add New Comp
              </Button>
            )}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ verticalAlign: "top" }}>
          <IconButton size="small" onClick={() => setCompsOpen(!compsOpen)}>
            {compsOpen ? <UpIcon /> : <DownIcon />}
          </IconButton>
        </TableCell>
        <TableCell colSpan={4} align="center">
          <Typography variant="h5">Components</Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5}>
          {isViewMode ? (
            <Collapse in={compsOpen} timeout="auto" unmountOnExit>
              <List>
                <ListItem>
                  <ListItemText primary="Row #" />
                  <ListItemText primary="Item" />
                  <ListItemText primary="Quantity" />
                  <ListItemText primary="Notes" />
                  <ListItemText primary="Build As Needed" />
                </ListItem>
                {components.map((comp, compIndex) => (
                  <CompLine
                    key={compIndex}
                    index={compIndex}
                    parentIndex={index}
                    id={comp.id}
                    control={control}
                    setValue={setValue}
                    remove={compRemove}
                    availItems={availItems}
                    allUoms={allUoms}
                    parentItemId={parentItemId}
                    isLoading={isLoading}
                    errors={errors}
                    isViewMode={isViewMode}
                    isEditMode={isEditMode}
                    isCreateMode={isCreateMode}
                  />
                ))}
              </List>
            </Collapse>
          ) : (
            <Collapse in={compsOpen} timeout="auto" unmountOnExit>
              <TableContainer component={Paper} sx={{ m: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "8%" }}>Row #</TableCell>
                      <TableCell sx={{ width: "20%" }}>Item</TableCell>
                      <TableCell sx={{ width: "20%" }}>Quantity</TableCell>
                      <TableCell sx={{ width: "20%" }}>Notes</TableCell>
                      <TableCell sx={{ width: "20%" }}>
                        Build As Needed
                      </TableCell>
                      <TableCell sx={{ width: "12%" }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {components.map((comp, compIndex) => (
                      <CompLine
                        key={compIndex}
                        index={compIndex}
                        parentIndex={index}
                        id={comp.id}
                        control={control}
                        setValue={setValue}
                        remove={compRemove}
                        availItems={availItems}
                        allUoms={allUoms}
                        parentItemId={parentItemId}
                        isLoading={isLoading}
                        errors={errors}
                        isViewMode={isViewMode}
                        isEditMode={isEditMode}
                        isCreateMode={isCreateMode}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          )}
        </TableCell>
      </TableRow>
    </>
  );
};

export const PageBOMForm: React.FC = () => {
  // const navigate = useNavigate();
  // const location = useLocation();

  // const searchParams = new URLSearchParams(location.search);
  // const id = searchParams.get("id");
  // const formMode = searchParams.get("mode") || "create";
  // const isViewMode = formMode === "view";
  // const isEditMode = Boolean(id) && formMode === "edit";
  // const isCreateMode = !id && formMode === "create";

  // const {
  //   control,
  //   handleSubmit,
  //   reset,
  //   formState: { errors, isSubmitting },
  //   watch,
  //   setValue,
  //   getValues,
  // } = useForm<BOMFormData>({
  //   resolver: yupResolver(bomSchema) as any,
  //   defaultValues: {
  //     name: "",
  //     item_id: "",
  //     revisions: [],
  //   },
  // });

  // const {
  //   data: thisBOMData,
  //   isLoading: thisBOMLoading,
  //   error: thisBOMError,
  // } = useTSQBOM(id || "");

  // const {
  //   data: thisBOMRevsResponse,
  //   isLoading: thisBOMRevsLoading,
  //   error: thisBOMRevsError,
  // } = useTSQBOMRevList(
  //   {
  //     skip: -1,
  //     limit: -1,
  //     active_status: "all",
  //     filters: [[{ field: "bom_id", operator: "==", value: id }]],
  //   },
  //   !isCreateMode && !!id
  // );

  // const {
  //   data: itemsResponse,
  //   isLoading: itemsLoading,
  //   error: itemsError,
  // } = useTSQItemList({
  //   skip: -1,
  //   limit: -1,
  //   active_status: "active",
  //   filters: [[]],
  // });

  // const {
  //   data: unitsResponse,
  //   isLoading: unitsLoading,
  //   error: unitsError,
  // } = useTSQUOMUnitList({
  //   skip: -1,
  //   limit: -1,
  //   active_status: "active",
  //   filters: [[]],
  // });

  // const createBOMMutation = useTSMCreateBOM();
  // const updateBOMMutation = useTSMUpdateBOM();

  // const [error, setError] = useState<string | null>(null);
  // const [items, setItems] = useState<ItemFull[]>([]);
  // const [units, setUnits] = useState<UOMUnitFull[]>([]);

  // const selectedItemId = watch("item_id");

  // const {
  //   fields: revisions,
  //   append: appendRev,
  //   remove: removeRev,
  // } = useFieldArray({
  //   control,
  //   name: "revisions",
  // });

  // const addNewRev = () => {
  //   appendRev({
  //     version_name: "",
  //     version_number: 999,
  //     effective_date: new Date().toISOString(),
  //     expiration_date: undefined,
  //     notes: undefined,
  //     is_new: true,
  //     is_edit: false,
  //     is_delete: false,
  //     components: [],
  //   });
  // };

  // const switchToViewMode = () => {
  //   navigate(`/manufacturing/boms/form?mode=view&id=${id}`);
  // };

  // const switchToEditMode = () => {
  //   navigate(`/manufacturing/boms/form?mode=edit&id=${id}`);
  // };

  // const switchToCreateMode = () => {
  //   navigate("/manufacturing/boms/form?mode=create");
  // };

  // useEffect(() => {
  //   if (
  //     thisBOMData &&
  //     thisBOMRevsResponse?.items &&
  //     (isEditMode || isViewMode)
  //   ) {
  //     reset({
  //       name: thisBOMData.name,
  //       item_id: thisBOMData.item.id,
  //       revisions: thisBOMRevsResponse.items.map((bomRev) => {
  //         return {
  //           id: bomRev.id,
  //           version_name: bomRev.version_name,
  //           version_number: bomRev.version_number,
  //           effective_date: bomRev.effective_date,
  //           expiration_date: bomRev.expiration_date,
  //           notes: bomRev.notes,
  //           components: bomRev.components.map((bomComp) => {
  //             return {
  //               id: bomComp.id,
  //               quantity: parseFloat(bomComp.quantity),
  //               offset: parseFloat(bomComp.offset),
  //               line: bomComp.line,
  //               build_as_needed: bomComp.build_as_needed,
  //               notes: bomComp.notes,
  //               component_item_id: bomComp.component_item.id,
  //               uom_unit_id: bomComp.uom_unit.id,
  //               is_new: false,
  //               is_edit: false,
  //               is_delete: false,
  //             };
  //           }),
  //           is_new: false,
  //           is_edit: false,
  //           is_delete: false,
  //         };
  //       }),
  //     });
  //   }
  // }, [thisBOMData, thisBOMRevsResponse, isEditMode, isViewMode, reset]);

  // useEffect(() => {
  //   if (itemsResponse?.items) {
  //     setItems(itemsResponse.items);
  //   }
  // }, [itemsResponse?.items]);

  // useEffect(() => {
  //   if (unitsResponse?.items) {
  //     setUnits(unitsResponse.items);
  //   }
  // }, [unitsResponse?.items]);

  // const onSubmit = async (data: BOMFormData) => {
  //   try {
  //     setError(null);
  //     console.log("SUBMITTED", data);
  //     if (isCreateMode) {
  //       const bomPayload: BOMCreate = {
  //         name: data.name,
  //         item_id: data.item_id,
  //         revisions: data.revisions.map((dataRev) => {
  //           const revPayload: BOMRevCreate = {
  //             version_name: dataRev.version_name,
  //             effective_date: dataRev.effective_date,
  //             expiration_date: dataRev.expiration_date,
  //             notes: dataRev.notes,
  //             components: dataRev.components.map((dataComp) => {
  //               const compPayload: BOMCompCreate = {
  //                 component_item_id: dataComp.component_item_id,
  //                 uom_unit_id: dataComp.uom_unit_id,
  //                 quantity: dataComp.quantity,
  //                 offset: dataComp.offset,
  //                 notes: dataComp.notes,
  //                 build_as_needed: dataComp.build_as_needed,
  //               };
  //               return compPayload;
  //             }),
  //           };
  //           return revPayload;
  //         }),
  //       };
  //       console.log("CREATE PAYLOAD", bomPayload);
  //       const new_bom = await createBOMMutation.mutateAsync(bomPayload);
  //       navigate(`/manufacturing/boms/form?mode=view&id=${new_bom.id}`);
  //     } else {
  //       if (!id) {
  //         throw new Error("BOM ID is required for update");
  //       }
  //       const bomPayload: BOMUpdate = {
  //         id: id,
  //         name: data.name,
  //         revs_to_update: data.revisions
  //           .filter((rev) => rev.is_edit && !rev.is_delete)
  //           .map((rev) => {
  //             const revPayload: BOMRevUpdate = {
  //               id: rev.id,
  //               version_name: rev.version_name,
  //               effective_date: rev.effective_date,
  //               expiration_date: rev.expiration_date,
  //               notes: rev.notes,
  //               updated_bom_components: rev.components
  //                 .filter((comp) => comp.is_edit && !comp.is_delete)
  //                 .map((comp) => {
  //                   const compPayload: BOMCompUpdate = {
  //                     id: comp.id,
  //                     component_item_id: comp.component_item_id,
  //                     uom_unit_id: comp.uom_unit_id,
  //                     quantity: comp.quantity,
  //                     offset: comp.offset,
  //                     notes: comp.notes,
  //                     build_as_needed: comp.build_as_needed,
  //                   };
  //                   return compPayload;
  //                 }),
  //               new_bom_components: rev.components
  //                 .filter((comp) => comp.is_new && !comp.is_delete)
  //                 .map((comp) => {
  //                   const compPayload: BOMCompCreate = {
  //                     bom_revision_id: rev.id,
  //                     component_item_id: comp.component_item_id,
  //                     uom_unit_id: comp.uom_unit_id,
  //                     quantity: comp.quantity,
  //                     offset: comp.offset,
  //                     notes: comp.notes,
  //                     build_as_needed: comp.build_as_needed,
  //                   };
  //                   return compPayload;
  //                 }),
  //               deleted_bom_components: rev.components
  //                 .filter((comp) => comp.is_delete)
  //                 .map((comp) => comp.id!),
  //             };
  //             return revPayload;
  //           }),
  //         revs_to_create: data.revisions
  //           .filter((rev) => rev.is_new && !rev.is_delete)
  //           .map((rev) => {
  //             const revPayload: BOMRevCreate = {
  //               bom_id: id,
  //               version_name: rev.version_name,
  //               effective_date: rev.effective_date,
  //               expiration_date: rev.expiration_date,
  //               notes: rev.notes,
  //               components: rev.components.map((dataComp) => {
  //                 const compPayload: BOMCompCreate = {
  //                   component_item_id: dataComp.component_item_id,
  //                   uom_unit_id: dataComp.uom_unit_id,
  //                   quantity: dataComp.quantity,
  //                   offset: dataComp.offset,
  //                   notes: dataComp.notes,
  //                   build_as_needed: dataComp.build_as_needed,
  //                 };
  //                 return compPayload;
  //               }),
  //             };
  //             return revPayload;
  //           }),
  //         revs_to_deactivate: data.revisions
  //           .filter((rev) => rev.is_delete === true && rev.id)
  //           .map((rev) => rev.id!),
  //       };
  //       console.log("UPDATE PAYLOAD", bomPayload);
  //       await updateBOMMutation.mutateAsync({ id: id, data: bomPayload });
  //       navigate(`/manufacturing/boms/form?mode=view&id=${id}`);
  //     }
  //   } catch (err: any) {
  //     console.error("Failed to save BOM:", err);
  //     setError(
  //       err.response?.data?.detail || err.message || "Failed to save BOM"
  //     );
  //   }
  // };

  // const handleCancel = () => {
  //   navigate("/manufacturing/boms");
  // };

  // const isLoading =
  //   thisBOMLoading || thisBOMRevsLoading || itemsLoading || unitsLoading;
  // const isSaving = createBOMMutation.isPending || updateBOMMutation.isPending;

  // if (isLoading && (isEditMode || isViewMode)) {
  //   return (
  //     <Box
  //       display="flex"
  //       justifyContent="center"
  //       alignItems="center"
  //       minHeight="400px"
  //     >
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  // return (
  //   <LocalizationProvider dateAdapter={AdapterDateFns}>
  //     <Box sx={{ p: 3 }}>
  //       <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
  //         <Button
  //           startIcon={<ArrowBackIcon />}
  //           onClick={handleCancel}
  //           variant="outlined"
  //         >
  //           Back to BOMs
  //         </Button>
  //         <Typography variant="h4" component="h1">
  //           {isCreateMode
  //             ? "Create BOM"
  //             : isViewMode
  //             ? `View BOM: ${thisBOMData?.name || ""}`
  //             : `Edit BOM: ${thisBOMData?.name || ""}`}
  //         </Typography>
  //         {formMode === "view" && (
  //           <Button
  //             variant="outlined"
  //             onClick={switchToCreateMode}
  //             startIcon={<AddIcon />}
  //           >
  //             Create New
  //           </Button>
  //         )}
  //         {isViewMode && (
  //           <Button
  //             variant="outlined"
  //             onClick={switchToEditMode}
  //             startIcon={<EditIcon />}
  //           >
  //             Edit
  //           </Button>
  //         )}
  //         {isEditMode && (
  //           <Button
  //             variant="outlined"
  //             onClick={switchToViewMode}
  //             startIcon={<VisibilityIcon />}
  //           >
  //             View
  //           </Button>
  //         )}
  //         <Button
  //           variant="outlined"
  //           onClick={(e) => {
  //             console.log(getValues());
  //           }}
  //         >
  //           DEBUG PRINT
  //         </Button>
  //       </Stack>

  //       {(error || thisBOMError || thisBOMRevsError) && (
  //         <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
  //           {error ||
  //             thisBOMError?.message ||
  //             thisBOMRevsError?.message ||
  //             "An unkown error occurred"}
  //         </Alert>
  //       )}

  //       <Paper sx={{ p: 3 }}>
  //         <form onSubmit={handleSubmit(onSubmit, console.error)}>
  //           <Stack spacing={3}>
  //             {/* Basic Information */}
  //             <Typography variant="h6" gutterBottom>
  //               Basic Information
  //             </Typography>

  //             <Box sx={{ display: "flex", gap: 2 }}>
  //               <Controller
  //                 name="name"
  //                 control={control}
  //                 render={({ field }) => (
  //                   <TextField
  //                     {...field}
  //                     label="BOM Name *"
  //                     fullWidth
  //                     disabled={isViewMode}
  //                     error={!!errors.name}
  //                     helperText={errors.name?.message}
  //                   />
  //                 )}
  //               />

  //               <Controller
  //                 name="item_id"
  //                 control={control}
  //                 render={({ field }) => (
  //                   <FormControl fullWidth disabled={isViewMode}>
  //                     <InputLabel>Item</InputLabel>
  //                     <Select {...field} label="Item">
  //                       {items
  //                         .filter((item) =>
  //                           [
  //                             "SUB ASSEMBLY",
  //                             "COMPONENT",
  //                             "FINISHED GOOD",
  //                           ].includes(item.item_type)
  //                         )
  //                         .map((item) => (
  //                           <MenuItem key={item.id} value={item.id}>
  //                             {item.number}
  //                           </MenuItem>
  //                         ))}
  //                     </Select>
  //                     {errors.item_id && (
  //                       <FormHelperText>
  //                         {errors.item_id.message}
  //                       </FormHelperText>
  //                     )}
  //                   </FormControl>
  //                 )}
  //               />
  //             </Box>

  //             {/* Revisions */}
  //             <Typography variant="h6" gutterBottom>
  //               BOM Revisions
  //             </Typography>

  //             <TableContainer component={Paper} sx={{ m: 2 }}>
  //               <Table>
  //                 <TableHead>
  //                   <TableRow>
  //                     <TableCell sx={{ width: "10%" }}>Rev #</TableCell>
  //                     <TableCell sx={{ width: "30%" }}>Name</TableCell>
  //                     <TableCell sx={{ width: "20%" }}>Dates</TableCell>
  //                     <TableCell sx={{ width: "20%" }}>Notes</TableCell>
  //                     <TableCell sx={{ width: "20%" }}></TableCell>
  //                   </TableRow>
  //                 </TableHead>
  //                 <TableBody>
  //                   {revisions.map((rev, revIndex) => (
  //                     <RevLine
  //                       key={revIndex}
  //                       index={revIndex}
  //                       id={rev.id}
  //                       control={control}
  //                       setValue={setValue}
  //                       remove={removeRev}
  //                       availItems={items}
  //                       allUoms={units}
  //                       parentItemId={selectedItemId}
  //                       isLoading={isLoading}
  //                       errors={errors}
  //                       isViewMode={isViewMode}
  //                       isEditMode={isEditMode}
  //                       isCreateMode={isCreateMode}
  //                     />
  //                   ))}
  //                 </TableBody>
  //               </Table>
  //             </TableContainer>

  //             {!isViewMode && (
  //               <Box
  //                 sx={{
  //                   display: "flex",
  //                   justifyContent: "space-between",
  //                   alignItems: "center",
  //                   mb: 2,
  //                 }}
  //               >
  //                 <Button
  //                   variant="outlined"
  //                   startIcon={<AddIcon />}
  //                   onClick={addNewRev}
  //                 >
  //                   Add Revision
  //                 </Button>
  //               </Box>
  //             )}

  //             {/* Form Actions */}
  //             {!isViewMode && (
  //               <Box
  //                 sx={{
  //                   display: "flex",
  //                   gap: 2,
  //                   justifyContent: "flex-end",
  //                   mt: 3,
  //                 }}
  //               >
  //                 <Button
  //                   variant="outlined"
  //                   startIcon={<CancelIcon />}
  //                   onClick={handleCancel}
  //                   disabled={isSubmitting || isLoading}
  //                 >
  //                   Cancel
  //                 </Button>
  //                 <Button
  //                   type="submit"
  //                   variant="contained"
  //                   startIcon={
  //                     isSaving ? <CircularProgress size={20} /> : <SaveIcon />
  //                   }
  //                   disabled={isSubmitting || isSaving}
  //                 >
  //                   {isSaving
  //                     ? "Saving..."
  //                     : isCreateMode
  //                     ? "Create BOM"
  //                     : "Update BOM"}
  //                 </Button>
  //               </Box>
  //             )}
  //           </Stack>
  //         </form>
  //       </Paper>
  //     </Box>
  //   </LocalizationProvider>
  // );
  return (
    <Box display="flex" alignItems={"center"} justifyItems={"center"}>
      <Typography variant="h1">
        This Page is Currently Undergoing Maintenance
      </Typography>
    </Box>
  );
};
